import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MUIAutoCompleteMultiSelectForm from "../../../inputs/MUIAutoCompleteMultiSelectForm";
import { ResponsiveRow } from "../../../../constants/layout.constants";
import ModalContainerModule from "../../../modules/modalContainer";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ModalAcceptButton } from "../../../../constants/component.constants";
import "dayjs/locale/en-gb";
import dayjs from "dayjs";
import {formatShortDateTime, isEmptyString, isNullUndefined} from "../../../../util/util";
import { useEffect, useState } from "react";
import { getMeterBillingPeriods } from "../../../../services/meterbilling.service";
import { getMeterBillingDays } from "../../../../services/list.service";
import {Add, Cached} from "@mui/icons-material";
import LatePenaltyConfig, {
  normalizeLatePenalty,
} from "../../../modules/latePenaltyConfig";

export default function AddMeterBilling(props) {
  /**
   *
   * REQUIRED PROPS
   *
   * AddDiv
   * EditDiv
   *
   * handleBillingClose
   * handleBillingDayChange
   * handleBillingItemAdd
   * handleBillingPeriodChange
   * handleMeterBillingDateChange
   * handleMetersChange
   * handleNextBillingDateChange
   *
   * setCustomerModal
   * updateBilling
   *
   * billingItemsTableRows
   * billingItemsTotal
   * facilityMeters
   * saveMeterBilling
   * selectedMeterbilling
   *
   */
  const [isSaving, setIsSaving] = useState(false);
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [rentDays, setRentDays] = useState([]);

  useEffect(() => {
    getMeterBillingDays().then((rentDays) => setRentDays(rentDays));
    getMeterBillingPeriods().then((billingPeriods) =>
      setBillingPeriods(billingPeriods),
    );
  }, []);

  const handleUpdateBilling = async () => {
    setIsSaving(true);
    try {
      await props.updateBilling();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalContainerModule
      size="md"
      accept={props.EditDiv ? handleUpdateBilling : props.saveMeterBilling}
      cancel={props.handleBillingClose}
      openModal={props.AddDiv || props.EditDiv}
      modalTitle={`  Billing ${
        props.selectedMeterbilling && props.selectedMeterbilling.billingCode
      }`}
      acceptButtonText={!props.EditDiv ? "Create" : "Update"}
      acceptLoading={props.EditDiv ? isSaving : undefined}
      acceptDisabled={props.EditDiv ? isSaving : undefined}
    >
      <ResponsiveRow
        sx={{
          height: "auto",
          margin: "auto",
        }}
      >
        <Typography
          sx={{ marginBottom: "5px" }}
          id="modal-modal-title"
          variant="h5"
          component="h4"
        >
          Billing Details
        </Typography>
        <ResponsiveRow
          container
          sx={{
            justifyContent: { xs: "center", lg: "flex-start" },
          }}
        >
          {!props.EditDiv ? (
            <FormControl
              required={true}
              sx={{ width: { xs: "100%", lg: "50%" }, marginBottom: "20px" }}
            >
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <DatePicker
                  label="Start date"
                  sx={{ width: { xs: "100%", lg: "90%" } }}
                  id={"effectiveDate"}
                  slotProps={{ textField: { required: true }}}
                  value={
                    props.selectedMeterbilling &&
                    dayjs(props.selectedMeterbilling.effectiveDate)
                  }
                  onChange={(newDate) => {
                    props.handleMeterBillingDateChange(newDate);
                  }}

                />
              </LocalizationProvider>
            </FormControl>
          ) : (
            <FormControl
              required={true}
              sx={{
                width: { xs: "100%", lg: "50%" },
                marginBottom: "20px",
              }}
            >
              {/* required */}
              <InputLabel>Start date</InputLabel>
              <OutlinedInput
                label="Start date"
                sx={{ width: { xs: "100%", md: "90%" } }}
                value={formatShortDateTime(props?.selectedMeterbilling?.effectiveDate)}
                id={"effectiveDate"}
                disabled
              />
            </FormControl>
          )}
          <FormControl
              sx={{
                width: { xs: "100%", lg: "50%" },
                height: "60px",
                marginBottom: "10px",
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
              }}
              key={props?.selectedMeterbilling?.customer?.name}
          >
            <InputLabel htmlFor="customer">Customer</InputLabel>
            <OutlinedInput
                label="Customer"
                id="customer"
                sx={{ width: "90%" }}
                value={
                  props?.selectedMeterbilling?.customer
                      ? props.selectedMeterbilling.customer.name
                      : ""
                }
                readOnly
                endAdornment={
                  <IconButton
                      onClick={() => props.setCustomerModal(true)}
                      sx={{
                        width: "40px",
                        height: "40px",
                        margin: "0 10px",
                        padding: "10px",
                        backgroundColor: "#037171",
                      }}
                      disabled={!(props.AddDiv === true || (props.selectedMeterbilling.statusCd === "DRF" && !props.selectedMeterbilling.hasTransactions))}
                  >
                    {(() => {
                      const hasCustomer = (!isNullUndefined(props?.selectedMeterbilling?.customer.name));

                      return !hasCustomer ? (
                          <Tooltip title="Add customer">
                            <Add sx={{ color: "white" }} />
                          </Tooltip>
                      ) : (
                          <Tooltip title="Switch customer">
                            <Cached sx={{ color: "white" }} />
                          </Tooltip>
                      );
                    })()}
                  </IconButton>
                }
            />
          </FormControl>
          <FormControl
            item
            required={true}
            sx={{
              width: { xs: "100%", lg: "50%" },
              marginBottom: "20px",
            }}
          >
            {/* required */}
            <InputLabel>Billing Frequency</InputLabel>
            <Select
              label="Billing Frequency"
              sx={{ width: { xs: "100%", md: "90%" } }}
              id={"billingPeriod"}
              value={
                !isEmptyString(
                  props.selectedMeterbilling &&
                    props.selectedMeterbilling.billingPeriod
                )
                  ? props.selectedMeterbilling &&
                    props.selectedMeterbilling.billingPeriod
                  : props.selectedBillingPeriod
              }
              onChange={(event) => {
                props.handleBillingPeriodChange(event);
              }}
            >
              {billingPeriods && billingPeriods.length > 0 ? (
                billingPeriods.map(function (billingPeriod, i) {
                  return (
                    <MenuItem key={i} value={billingPeriod.value}>
                      {billingPeriod.label}
                    </MenuItem>
                  );
                }, this)
              ) : (
                <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
              )}
            </Select>
          </FormControl>
          {props.selectedMeterbilling &&
            props.selectedMeterbilling.billingPeriod === "MNTH" && (
              <FormControl
                item
                required={true}
                sx={{
                  width: { xs: "100%", lg: "50%" },
                  marginBottom: "20px",
                }}
              >
                {/* required */}
                <InputLabel>Billing Due Day</InputLabel>
                <Select
                  label="Billing Due Day"
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"billingDay"}
                  value={
                    props.selectedMeterbilling?.billingDay !== undefined && 
                    props.selectedMeterbilling?.billingDay !== null && 
                    props.selectedMeterbilling?.billingDay !== ""
                      ? String(props.selectedMeterbilling.billingDay)
                      : props.selectedBillingDay || ""
                  }
                  onChange={(event) => props.handleBillingDayChange(event)}
                  defaultValue={[""]}
                >
                  {rentDays && rentDays.length > 0 ? (
                    rentDays.map(function (weekDay, i) {
                      return (
                        <MenuItem key={i} value={weekDay.value}>
                          {weekDay.label}
                        </MenuItem>
                      );
                    }, this)
                  ) : (
                    <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          {props.selectedMeterbilling &&
            (props.selectedMeterbilling.billingPeriod === "ANU" ||
              props.selectedMeterbilling.billingPeriod === "QT") && (
              <FormControl
                item
                required={true}
                sx={{
                  width: { xs: "100%", lg: "50%" },
                  marginBottom: "20px",
                }}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"
                >
                  <DatePicker
                    label="Next Billing Date"
                    sx={{ width: { xs: "100%", lg: "90%" } }}
                    id={"nextBillingDate"}
                    slotProps={{ textField: { required: true }}}
                    value={
                      props.selectedMeterbilling?.billingDate && 
                      !isEmptyString(props.selectedMeterbilling.billingDate)
                        ? dayjs(props.selectedMeterbilling.billingDate)
                        : props.selectedBillingDate ? dayjs(props.selectedBillingDate) : null
                    }
                    onChange={(newDate) =>
                      props.handleNextBillingDateChange(newDate)
                    }

                  />
                </LocalizationProvider>
              </FormControl>
            )}
            {!props.EditDiv ? (
                <FormControl
                    required={true}
                    sx={{
                        width: { xs: "100%", lg: "50%" },
                        marginBottom: "20px",
                    }}
                >
                    <MUIAutoCompleteMultiSelectForm
                        label="Meter/Meters"
                        title="meter"
                        options={props.facilityMeters || []}
                        selectedOptions={
                            props.selectedMeterbilling &&
                            props.selectedMeterbilling.meterIds &&
                            props.selectedMeterbilling.meterIds.length > 0
                                ? props.facilityMeters.filter(meter =>
                                    props.selectedMeterbilling.meterIds.includes(meter.value)
                                )
                                : []
                        }
                        onSelect={(event, newValue) => props.handleMetersChange(event, newValue)}
                        disabled={props.EditDiv}
                        placeholder="Search and select meters..."
                        limitTags={3}
                        isRequired={true}
                        sx={{ width: { xs: "100%", md: "90%" } }}
                    />
                </FormControl>
            ) : (
                <FormControl
                    sx={{
                        width: { xs: "100%", lg: "50%" },
                        marginBottom: "20px",
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            border: '1px solid',
                            borderColor: 'grey.400',
                            borderRadius: 1,
                            p: "14px",
                            width: { xs: "100%", md: "90%" },
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                top: -10, // Adjust based on font size to center on border
                                left: 12,
                                bgcolor: 'background.paper', // Must match container background
                                px: 0.5,
                            }}
                        >
                            Meters
                        </Typography>
                        {props?.selectedMeterbilling?.meterIds?.length > 0 ? (
                            props.selectedMeterbilling.meterIds.map((meterId) => {
                                // Find meter name from billingChargeItems or meterBillingMeters
                                let meterName = meterId;
                                if (props?.meterBillingMeters?.length > 0) {
                                    const meterOption = props.meterBillingMeters.find(
                                        (m) => m.value === meterId
                                    );
                                    if (meterOption) {
                                        meterName = meterOption.label;
                                    }
                                }
                                // Show delete button unless only one meter is remaining
                                const canRemove = props.selectedMeterbilling.meterIds.length > 1;

                                return (
                                    <Box
                                        key={meterId}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <Chip
                                            label={meterName}
                                            color="default"
                                            variant="filled"
                                            sx={{ backgroundColor: "transparent" }}
                                        />
                                        {canRemove && props.onMeterRemove && (
                                            <IconButton
                                                size="small"
                                                onClick={() => props.onMeterRemove(meterId, meterName)}
                                                color="error"
                                                title="Remove meter"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No meters attached
                            </Typography>
                        )}
                        {props.selectedMeterbilling && (
                            <>
                                {props.onMeterAdd && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            backgroundColor: "#e3f2fd",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: "#bbdefb",
                                            },
                                        }}
                                        onClick={props.onMeterAdd}
                                    >
                                        <AddIcon fontSize="small" color="primary" />
                                        <Typography
                                            variant="caption"
                                            color="primary"
                                            sx={{ fontWeight: "medium" }}
                                        >
                                            Add Meter
                                        </Typography>
                                    </Box>
                                )}
                                {props.selectedMeterbilling.meterIds &&
                                    props.selectedMeterbilling.meterIds.length > 1 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ width: "100%", marginTop: "4px" }}
                                        >
                                            Click delete icon to remove or Add Meter to add a new meter
                                        </Typography>
                                    )}
                            </>
                        )}
                    </Box>
                </FormControl>
            )}
        </ResponsiveRow>
      </ResponsiveRow>
      <br />

      <ResponsiveRow sx={{ height: "auto", margin: "auto", padding: "10px" }}>
        <Typography
          sx={{ marginBottom: "5px" }}
          id="modal-modal-title"
          variant="h5"
          component="h4"
        >
          Billing Charges
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {(props.EditDiv || props.AddDiv) && (
                  <TableCell sx={{ width: "10%" }}>Meter</TableCell>
                )}
                <TableCell sx={{ width: "30%" }}>Charge Type *</TableCell>
                <TableCell sx={{ width: "20%" }}>Amount *</TableCell>
                <TableCell sx={{ width: "30%" }}>Start Date</TableCell>
                <TableCell sx={{ width: "10%" }}>Taxed?</TableCell>
                <TableCell sx={{ width: "10%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{props.billingItemsTableRows}</TableBody>
          </Table>
        </TableContainer>
        <Alert severity="info" sx={{ width: "100%" }}>
          <AlertTitle>Total: {props.billingItemsTotal}</AlertTitle>
        </Alert>
        <ResponsiveRow
          container
          sx={{
            justifyContent: { xs: "center", lg: "flex-end" },
            width: "100%",
          }}
        >
          <ModalAcceptButton
            onClick={() => props.handleBillingItemAdd()}
            sx={{
              width: { xs: "100%", lg: "200px" },
            }}
          >
            Add row
          </ModalAcceptButton>
        </ResponsiveRow>
      </ResponsiveRow>
      <br />
      <LatePenaltyConfig
        value={normalizeLatePenalty(props.selectedMeterbilling?.latePenalty)}
        onChange={(latePenalty) =>
          props.setSelectedMeterbilling({
            ...props.selectedMeterbilling,
            latePenalty,
          })
        }
      />
    </ModalContainerModule>
  );
}
